import {
  createMutationStore,
  createQueryStore,
} from '@ferreri/minato-react-utils';
import {
  getCombinedConfig,
  usePublicationIdQueryParam,
} from '../publications/publications.store';
import {
  deletePublicationImages,
  getCoverPublicationImage,
  getPublicationImages,
  postPublicationImages,
  putPublicationImages,
} from './publication-images.client';

type FormData = {
  publication_id: string;
  files: {
    to_create: [{ file: File[] }];
    to_delete: number[];
    to_update: { id: number; file: File }[];
  };
};

export const usePublicationImages = () => {
  const publicationId = usePublicationIdQueryParam();
  return createQueryStore({
    queryKey: ['publication_images', publicationId],
    queryFn: () => getPublicationImages(publicationId),
    enabled: !!publicationId,
  });
};

export const useCoverPublicationImage = (
  publicationId: string,
  coverImageId: number,
) => {
  return createQueryStore({
    queryKey: ['cover_publication_image', publicationId],
    queryFn: () => getCoverPublicationImage(publicationId),
    enabled: !!coverImageId,
  });
};

export const useSavePublicationImages = () => {
  const {
    mutateAsync: createPublicationImages,
    isLoading: isCreateLoading,
    isSuccess: isCreateSuccess,
  } = useCreatePublicationImages();
  const {
    mutateAsync: updatePublicationImages,
    isSuccess: isUpdateSuccess,
    isLoading: isUpdateLoading,
  } = useUpdatePublicationImages();
  const {
    mutateAsync: deletePublicationImages,
    isSuccess: isDeleteSuccess,
    isLoading: isDeleteLoading,
  } = useDeletePublicationImages();

  const isLoading = isCreateLoading || isUpdateLoading || isDeleteLoading;
  const isSuccess = isCreateSuccess || isUpdateSuccess || isDeleteSuccess;

  const mutate = async ({ files, publication_id }: FormData) => {
    const mutations = [
      ...(files?.to_delete || []).map((assetId) =>
        deletePublicationImages({ publication_id, asset_id: assetId }),
      ),
      ...(files?.to_update || []).map(({ file, id }) =>
        updatePublicationImages({ file, publication_id, asset_id: id }),
      ),
      ...(files?.to_create || []).map(({ file }) =>
        createPublicationImages({ file, publication_id }),
      ),
    ].reduce((acc, mutation) => acc.concat(mutation), []);
    await Promise.all(mutations);
  };

  return {
    mutate,
    isLoading,
    isSuccess,
  };
};

export const useDeletePublicationImages = (config = null) => {
  return createMutationStore({
    mutationFn: (params) =>
      deletePublicationImages(params.publication_id, params.asset_id),
    ...getCombinedConfig(config),
  });
};

export const useCreatePublicationImages = (config = null) => {
  return createMutationStore({
    mutationFn: (params) => postPublicationImages(params),
    ...getCombinedConfig(config),
  });
};

export const useUpdatePublicationImages = (config = null) => {
  return createMutationStore({
    mutationFn: (params) => putPublicationImages(params),
    ...getCombinedConfig(config),
  });
};
